import React, {useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {KP11LinkGCardComponent} from './kp-11Link-cards/kp-11Link-g-card'
import {handleThryDesign2IfPlusSelected} from '../../kp-11Link-utility/kp11-utility'
import {
  selectItem,
  removeItem,
  updateGroupCustomFields as updateGroupCustomFieldsAction,
  updateCatalogGroups as updateCatalogGroupsAction,
  selectKP11CatalogGroups,
  selectKP11AllGroupCustomFieldsMap,
  selectIsKP11DataLoaded,
  selectDisabledItems,
  updateParentCollapseStatus,
  getCatalogCollapseStatus,
  getParentCardCollapseStatus,
  getCartItems,
  updateCatalogCollapseStatus,
  selectKP11SelectCartCentersNApps,
} from '../../../../../app-store/kp11CartSlice'

export const KP11LinkCatalogCardWrapperComponent = ({
  selectedCountry = 'US',
  catalogGroups: initialCatalogGroups,
  setCatalogGroups: externalSetCatalogGroups,
  allGroupCustomFieldsMap: initialAllGroupCustomFieldsMap,
  setAllGroupCustomFieldsMap: externalSetAllGroupCustomFieldsMap,
  defaultAllGroupCustomFieldsMap,
  centersNAppVList,
  defaultCartCentersNApps,
}) => {
  const dispatch = useDispatch()

  // Get data
  const isDataLoaded = useSelector(selectIsKP11DataLoaded)
  const disabledItems = useSelector(selectDisabledItems)
  const catalogGroups = useSelector(selectKP11CatalogGroups)
  const allGroupCustomFieldsMap = useSelector(selectKP11AllGroupCustomFieldsMap)
  const catalogCardCollapseStatus = useSelector(getCatalogCollapseStatus)
  const parentCardCollapseStatus = useSelector(getParentCardCollapseStatus)
  const cartItems = useSelector(getCartItems)
  const cartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)

  // Derive addedItemsToCart from cartItems - shows which groups have items added
  const addedItemsToCart = useMemo(() => {
    const result = {}
    Object.values(cartItems || {}).forEach(item => {
      if (item?.groupKey) {
        result[item.groupKey] = true
      }
    })
    return result
  }, [cartItems])

  // True when any Keap plan is in centers — new, pre-existing, or legacy variant.
  // Uses .includes('keap') to cover all code patterns without relying on hardcoded lists.
  const hasKeapPlanInCart = useMemo(() => {
    return (cartCentersAndApps?.centers || []).some(c =>
      c.code.includes('keap'),
    )
  }, [cartCentersAndApps])

  const result = useMemo(() => {
    let grouped = {}
    catalogGroups.forEach(({items}, index) => {
      items.forEach(item => {
        const group = item.uigroup ? item.uigroup : 'Thryv Addons'
        if (!grouped[group]) {
          grouped[group] = {...item, items: []}
        }
        grouped[group].items.push(item)
      })
    })

    return Object.keys(grouped)
      .map(uigroup => {
        const key = uigroup.replaceAll(' ', '').toLowerCase()
        return {
          ...grouped[uigroup],
          uigroup,
          key,
        }
      })
      .sort(
        (a, b) =>
          (typeof a.groupSequence === 'number' ? a.groupSequence : 9999) -
          (typeof b.groupSequence === 'number' ? b.groupSequence : 9999),
      )
  }, [catalogGroups])

  // Don't render until data is loaded
  if (!isDataLoaded) {
    return null
  }

  // Get disabled state from Redux
  //const isDisabled = disabledItems[item.code] || false

  const handleExpand = groupKey => {
    if (groupKey === 'validateEmail') return
    const updatedCollapseStatus = !parentCardCollapseStatus[groupKey]

    Object.entries(catalogCardCollapseStatus).forEach(([key, value]) => {
      if (value === true) {
        dispatch(updateCatalogCollapseStatus(key))
      }
    })
    dispatch(
      updateParentCollapseStatus({groupKey, collapse: updatedCollapseStatus}),
    )
  }

  const handleSelectItem = item => {
    dispatch(selectItem({item}))

    if (item.groupKey) {
      const currentParentCollapseStatus =
        parentCardCollapseStatus[item.groupKey]

      if (
        currentParentCollapseStatus !== true &&
        (Object.keys(cartItems) > 0 || cartItems[item.code]?.code !== item.code)
      ) {
        dispatch(
          updateParentCollapseStatus({
            groupKey: item.groupKey,
            collapse: true,
          }),
        )
      }

      if (item.groupKey === 'marketingCenter') {
        handleThryDesign2IfPlusSelected({
          item,
          filteredGroup: catalogGroups,
          selectedCountry,
          setCatalogGroups: updater => {
            const newCatalogGroups =
              typeof updater === 'function' ? updater(catalogGroups) : updater
            dispatch(updateCatalogGroupsAction(newCatalogGroups))
          },
        })
      }
    }
  }

  const handleRemove = groupKey => {
    dispatch(removeItem({groupKey}))
  }

  const handleGroupCustomFields = (groupKey, updatedFields) => {
    dispatch(updateGroupCustomFieldsAction({groupKey, updatedFields}))
  }

  const renderCardComponent = data => {
    if (data?.uigroup !== 'Hidden') {
      let title = data.uigroup
      let key = data.uigroup.replaceAll(' ', '').toLowerCase()
      let section =
        data?.prod_type === 'OneTimeFees'
          ? 'oneTimeFees'
          : data?.prod_type === 'Center'
          ? 'centers'
          : 'apps'
      const item = {
        title,
        key,
        section,
        ...data,
      }
      const isParentCollapsed = parentCardCollapseStatus[item.key]

      return (
        <KP11LinkGCardComponent
          selectedCountry={selectedCountry}
          title={item.title}
          groupKey={item.key}
          group={item}
          items={item.items.map(i => ({
            ...i,
            groupKey: item.key,
            section:
              i?.prod_type === 'OneTimeFees'
                ? 'oneTimeFees'
                : i?.prod_type === 'Center'
                ? 'centers'
                : 'apps',
          }))}
          expanded={!isParentCollapsed}
          onExpand={() => handleExpand(item.key)}
          onSelectItem={handleSelectItem}
          groupCustomFields={allGroupCustomFieldsMap[item.key]}
          onGroupCustomFieldsUpdate={updateData =>
            handleGroupCustomFields(item.key, updateData)
          }
          disabled={
            item.key === 'keapadd-ons'
              ? !hasKeapPlanInCart
              : addedItemsToCart[item.key]
              ? false
              : (item.items || []).length > 0 &&
                item.items.every(groupItem => disabledItems[groupItem.code])
          }
          disabledItems={disabledItems}
          onRemove={() => handleRemove(item.key)}
          hasItem={!!addedItemsToCart[item.key]}
          showAdd={!addedItemsToCart[item.key]}
          addedToCart={!!addedItemsToCart[item.key]}
          catalogGroups={catalogGroups}
          // setExpanded={setExpanded}
        />
      )
    }
  }
  return (
    <>
      {result.map(item => (
        <React.Fragment key={item.uigroup}>
          {renderCardComponent(item)}
        </React.Fragment>
      ))}
    </>
  )
}
